import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

// API imports
import { getDoctors } from "@/api/doctors";
import { getServices } from "@/api/services";
import { getPackages } from "@/api/packages";
import { BlogPost, BlogCategory, getPosts, getCategories, adaptPost } from "@/api/blog";
import { getHomeContent, updateHomeContent } from "@/api/cms_home";
import { getFaqs, getFaqCategories, massSaveFaqs } from "@/api/faq";

// CMS API imports
import { getAboutCMS, updateAboutCMS } from "@/api/about";
import { getOffersPageConfig, updateOffersPageConfig } from "@/api/offers";
import { getContactCMS, updateContactCMS } from "@/api/contact_cms";
import { getSearchCMS, updateSearchCMS } from "@/api/search_cms";
import { getSettings, updateSettings } from "@/api/settings";

import { 
  AboutHeroData, AboutStoryData, AboutMission, AboutValue, AboutTimelineEvent, AboutTeamMember, AboutAward,
  OffersHeroData, SeasonalOffer, FlashDeal, HowToRedeemStep, OffersNotifyData,
  ContactHeroData, ContactMethod, ContactFormConfig, ContactMapConfig, ContactWorkingHours, ContactSocialLink, ContactCtaBanner, ContactFaqTeaser,
  SearchHeroData, PopularSearch, QuickLink, SearchCTAData, SearchResultsConfig,
  ClinicInfo, WorkingHours, SocialMedia, EmergencyContact, SeoSettings, PixelSettings, SecuritySettings,
  defaultAboutHero, defaultAboutStory, defaultOffersHero, defaultOffersNotify, defaultContactHero, defaultContactForm, defaultContactMap, defaultContactCta, defaultContactFaq, defaultSearchHero, defaultSearchCTA, defaultSearchResultsConfig, defaultClinicInfo, defaultWorkingHours, defaultSocialMedia, defaultEmergencyContact, defaultSeoSettings, defaultPixelSettings, defaultSecuritySettings
} from "@/types/cms";

// Legacy Mocks imports for types
import { PackageCategory } from "@/mocks/packagesData";
export type { PackageCategory };
import { type FAQItem, type FAQCategory } from "@/mocks/faqData";
import { testimonialsData as initialTestimonials, testimonialsConfig as initialTestimonialsConfig, testimonialsStats as initialTestimonialsStats } from "@/mocks/testimonialsData";
import { cmsContent as initialCmsContent, type CMSContent } from "@/mocks/cmsData";

// --- Types ---
export interface Doctor {
  id: string;
  doctor_id: string;
  name: string;
  nameEn?: string;
  name_en?: string;
  specialty: string;
  specialtyEn?: string;
  specialty_en?: string;
  experience: string;
  experienceEn?: string;
  experience_en?: string;
  image: string;
  rating: number;
  reviewsCount: number;
  title: string;
  titleEn?: string;
  title_en?: string;
  education: string;
  educationEn?: string;
  education_en?: string;
  bio: string;
  bioEn?: string;
  bio_en?: string;
  languages: string[];
  languagesEn?: string[];
  languages_en?: string[];
  availableDays: string[];
  consultationFee?: string;
  consultationFeeEn?: string;
  consultationFee_en?: string;
  showPrice?: boolean;
  specializations: string[];
  specializationsEn?: string[];
  specializations_en?: string[];
  achievements: string[];
  achievementsEn?: string[];
  achievements_en?: string[];
  reviews: any[];
}

export interface ServiceDetail {
  id: string;
  service_id: string;
  name: string;
  nameEn?: string;
  name_en?: string;
  tagline: string;
  taglineEn?: string;
  tagline_en?: string;
  description: string;
  descriptionEn?: string;
  description_en?: string;
  longDescription: string;
  longDescriptionEn?: string;
  longDescription_en?: string;
  icon: string;
  color: string;
  accentColor: string;
  image: string;
  heroImage: string;
  category: string;
  categoryEn?: string;
  category_en?: string;
  stats: any[];
  procedures: any[];
  prices?: any[];
  doctors: any[];
  faqs: any[];
  relatedServices: string[];
  price?: string;
  duration: string;
  status: string;
  showPrice?: boolean;
}

export interface MedicalPackage {
  id: string;
  package_id: string;
  name: string;
  nameEn?: string;
  name_en?: string;
  category: PackageCategory;
  categoryEn?: string;
  category_en?: string;
  price?: number;
  originalPrice?: number;
  showPrice?: boolean;
  badge: string;
  badgeEn?: string;
  badge_en?: string;
  icon: string;
  features: string[];
  featuresEn?: string[];
  features_en?: string[];
  accentColor: string;
  description: string;
  descriptionEn?: string;
  description_en?: string;
  duration: string;
  durationEn?: string;
  duration_en?: string;
  targetAudience: string;
  targetAudienceEn?: string;
  targetAudience_en?: string;
  preparation: string[];
  preparationEn?: string[];
  preparation_en?: string[];
  includes: { label: string; icon: string; labelEn?: string }[];
  includesEn?: { label: string; icon: string }[];
  faqs: any[];
  status: string;
  image: string;
  is_active?: boolean;
}

// ----------------------------------------------------------------------
// LocalStorage helpers for legacy stuff like testimonials
// ----------------------------------------------------------------------
function readLS<T>(key: string, defaultVal: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultVal;
  } catch {
    return defaultVal;
  }
}
function writeLS<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch { /* silent */ }
}

// ----------------------------------------------------------------------
// Context Interface
// ----------------------------------------------------------------------

interface DataContextType {
  doctors: Doctor[];
  doctorsLoading: boolean;
  doctorsError: string | null;
  reloadDoctors: () => Promise<void>;

  services: ServiceDetail[];
  servicesLoading: boolean;
  servicesError: string | null;
  reloadServices: () => Promise<void>;

  packages: MedicalPackage[];
  packagesLoading: boolean;
  packagesError: string | null;
  reloadPackages: () => Promise<void>;

  posts: BlogPost[];
  allPosts: BlogPost[];
  blogCategories: BlogCategory[];
  blogLoading: boolean;
  blogError: string | null;
  reloadBlog: () => Promise<void>;

  // FAQs
  faqs: FAQItem[];
  faqCategories: FAQCategory[];
  faqsLoading: boolean;
  faqsError: string | null;
  reloadFAQs: () => Promise<void>;
  saveFAQs: (newFaqs: FAQItem[], newCats: FAQCategory[]) => Promise<void>;
  resetFAQs: () => void;

  // About CMS
  aboutHero: AboutHeroData;
  aboutStory: AboutStoryData;
  aboutMission: AboutMission[];
  aboutValues: AboutValue[];
  aboutTimeline: AboutTimelineEvent[];
  aboutTeam: AboutTeamMember[];
  aboutAwards: AboutAward[];
  aboutLoading: boolean;
  reloadAbout: () => Promise<void>;
  saveAbout: (data: {
    hero: AboutHeroData;
    story: AboutStoryData;
    mission: AboutMission[];
    values: AboutValue[];
    timeline: AboutTimelineEvent[];
    team: AboutTeamMember[];
    awards: AboutAward[];
  }) => Promise<void>;

  // Contact CMS
  contactHero: ContactHeroData;
  contactMethods: ContactMethod[];
  contactFormConfig: ContactFormConfig;
  contactMapConfig: ContactMapConfig;
  contactWorkingHours: ContactWorkingHours;
  contactSocialLinks: ContactSocialLink[];
  contactCtaBanner: ContactCtaBanner;
  contactFaqTeaser: ContactFaqTeaser;
  contactLoading: boolean;
  reloadContact: () => Promise<void>;
  saveContact: (data: {
    hero: ContactHeroData;
    methods: ContactMethod[];
    form: ContactFormConfig;
    map: ContactMapConfig;
    hours: ContactWorkingHours;
    social: ContactSocialLink[];
    cta: ContactCtaBanner;
    faq: ContactFaqTeaser;
  }) => Promise<void>;

  // Offers CMS
  offersHero: OffersHeroData;
  offersSeasonal: SeasonalOffer[];
  offersFlash: FlashDeal[];
  offersRedeem: HowToRedeemStep[];
  offersNotify: OffersNotifyData;
  offersLoading: boolean;
  reloadOffers: () => Promise<void>;
  saveOffers: (data: {
    hero: OffersHeroData;
    seasonal: SeasonalOffer[];
    flash: FlashDeal[];
    redeem: HowToRedeemStep[];
    notify: OffersNotifyData;
  }) => Promise<void>;

  // Settings CMS
  clinicInfo: ClinicInfo;
  clinicHours: WorkingHours;
  clinicSocial: SocialMedia;
  clinicEmergency: EmergencyContact;
  clinicSeo: SeoSettings;
  clinicPixels: PixelSettings;
  clinicSecurity: SecuritySettings;
  settingsLoading: boolean;
  reloadSettings: () => Promise<void>;
  saveSettings: (data: {
    info: ClinicInfo;
    hours: WorkingHours;
    social: SocialMedia;
    emergency: EmergencyContact;
    seo: SeoSettings;
    pixels: PixelSettings;
    security: SecuritySettings;
  }) => Promise<void>;

  // Testimonials
  testimonials: typeof initialTestimonials;
  testimonialsConfig: typeof initialTestimonialsConfig;
  testimonialsStats: typeof initialTestimonialsStats;
  saveTestimonials: (data: typeof initialTestimonials, config: typeof initialTestimonialsConfig) => Promise<void>;
  resetTestimonials: () => void;

  // Search CMS
  searchHero: SearchHeroData;
  searchPopular: PopularSearch[];
  searchQuickLinks: QuickLink[];
  searchCTA: SearchCTAData;
  searchResultsConfig: SearchResultsConfig;
  searchLoading: boolean;
  reloadSearch: () => Promise<void>;
  saveSearch: (data: {
    hero: SearchHeroData;
    popular: PopularSearch[];
    quickLinks: QuickLink[];
    cta: SearchCTAData;
    resultsConfig: SearchResultsConfig;
  }) => Promise<void>;

  // Homepage CMS
  homeContent: CMSContent;
  homeContentLoading: boolean;
  reloadHomeContent: () => Promise<void>;
  saveHomeContent: (data: CMSContent) => Promise<void>;
  resetHomeContent: () => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // API State
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [doctorsError, setDoctorsError] = useState<string | null>(null);

  const [services, setServices] = useState<ServiceDetail[]>([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState<string | null>(null);

  const [packages, setPackages] = useState<MedicalPackage[]>([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesError, setPackagesError] = useState<string | null>(null);

  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  const [blogCategories, setBlogCategories] = useState<BlogCategory[]>([]);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogError, setBlogError] = useState<string | null>(null);

  // FAQs
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [faqCategories, setFaqCategories] = useState<FAQCategory[]>([]);
  const [faqsLoading, setFaqsLoading] = useState(false);
  const [faqsError, setFaqsError] = useState<string | null>(null);

  // About CMS State
  const [aboutLoading, setAboutLoading] = useState(false);
  const [aboutHeroState, setAboutHero] = useState<AboutHeroData>(defaultAboutHero);
  const [aboutStoryState, setAboutStory] = useState<AboutStoryData>(defaultAboutStory);
  const [aboutMissionState, setAboutMission] = useState<AboutMission[]>([]);
  const [aboutValuesState, setAboutValues] = useState<AboutValue[]>([]);
  const [aboutTimelineState, setAboutTimeline] = useState<AboutTimelineEvent[]>([]);
  const [aboutTeamState, setAboutTeam] = useState<AboutTeamMember[]>([]);
  const [aboutAwardsState, setAboutAwards] = useState<AboutAward[]>([]);

  // Contact CMS State
  const [contactLoading, setContactLoading] = useState(false);
  const [contactHeroState, setContactHero] = useState<ContactHeroData>(defaultContactHero);
  const [contactMethodsState, setContactMethods] = useState<ContactMethod[]>([]);
  const [contactFormConfigState, setContactFormConfig] = useState<ContactFormConfig>(defaultContactForm);
  const [contactMapConfigState, setContactMapConfig] = useState<ContactMapConfig>(defaultContactMap);
  const [contactWorkingHoursState, setContactWorkingHours] = useState<ContactWorkingHours>([]);
  const [contactSocialLinksState, setContactSocialLinks] = useState<ContactSocialLink[]>([]);
  const [contactCtaBannerState, setContactCtaBanner] = useState<ContactCtaBanner>(defaultContactCta);
  const [contactFaqTeaserState, setContactFaqTeaser] = useState<ContactFaqTeaser>(defaultContactFaq);

  // Offers CMS State
  const [offersLoading, setOffersLoading] = useState(true);
  const [offersHeroState, setOffersHero] = useState<OffersHeroData>(defaultOffersHero);
  const [offersSeasonalState, setOffersSeasonal] = useState<SeasonalOffer[]>([]);
  const [offersFlashState, setOffersFlash] = useState<FlashDeal[]>([]);
  const [offersRedeemState, setOffersRedeem] = useState<HowToRedeemStep[]>([]);
  const [offersNotifyState, setOffersNotify] = useState<OffersNotifyData>(defaultOffersNotify);

  // Clinic Settings State
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [clinicInfoState, setClinicInfo] = useState<ClinicInfo>(defaultClinicInfo);
  const [clinicHoursState, setClinicHours] = useState<WorkingHours>(defaultWorkingHours);
  const [clinicSocialState, setClinicSocial] = useState<SocialMedia>(defaultSocialMedia);
  const [clinicEmergencyState, setClinicEmergency] = useState<EmergencyContact>(defaultEmergencyContact);
  const [clinicSeoState, setClinicSeo] = useState<SeoSettings>(defaultSeoSettings);
  const [clinicPixelsState, setClinicPixels] = useState<PixelSettings>(defaultPixelSettings);
  const [clinicSecurityState, setClinicSecurity] = useState<SecuritySettings>(defaultSecuritySettings);

  // Testimonials
  const [testimonialsState, setTestimonials] = useState<any[]>(initialTestimonials);
  const [testimonialsConfigState, setTestimonialsConfig] = useState<any>(initialTestimonialsConfig);

  // Search CMS State
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchHeroState, setSearchHero] = useState<SearchHeroData>(defaultSearchHero);
  const [searchPopularState, setSearchPopular] = useState<PopularSearch[]>([]);
  const [searchQuickLinksState, setSearchQuickLinks] = useState<QuickLink[]>([]);
  const [searchCTAState, setSearchCTA] = useState<SearchCTAData>(defaultSearchCTA);
  const [searchResultsConfigState, setSearchResultsConfig] = useState<SearchResultsConfig>(defaultSearchResultsConfig);

  // Home Page CMS
  const [homeContentLoading, setHomeContentLoading] = useState(false);
  const [homeContentState, setHomeContent] = useState<CMSContent>(() => readLS("cms_home_data", initialCmsContent));

  // --- Loaders ---
  const reloadDoctors = useCallback(async () => {
    setDoctorsLoading(true);
    setDoctorsError(null);
    try {
      const data = await getDoctors();
      const mapped = data.map(d => ({
        id: d.id,
        doctor_id: d.doctor_id,
        name: d.name,
        nameEn: (d as any).name_en || (d as any).nameEn || "",
        name_en: (d as any).name_en || (d as any).nameEn || "",
        specialty: d.specialty || "",
        specialtyEn: (d as any).specialty_en || (d as any).specialtyEn || "",
        specialty_en: (d as any).specialty_en || (d as any).specialtyEn || "",
        experience: d.experience || "",
        experienceEn: (d as any).experience_en || (d as any).experienceEn || "",
        experience_en: (d as any).experience_en || (d as any).experienceEn || "",
        image: d.image || "",
        rating: d.rating || 5.0,
        reviewsCount: d.reviews_count || 0,
        title: d.title || "",
        titleEn: (d as any).title_en || (d as any).titleEn || "",
        title_en: (d as any).title_en || (d as any).titleEn || "",
        education: d.education || "",
        educationEn: (d as any).education_en || (d as any).educationEn || "",
        education_en: (d as any).education_en || (d as any).educationEn || "",
        bio: d.bio || "",
        bioEn: (d as any).bio_en || (d as any).bioEn || "",
        bio_en: (d as any).bio_en || (d as any).bioEn || "",
        languages: d.languages || [],
        languagesEn: (d as any).languages_en || (d as any).languagesEn || [],
        availableDays: d.available_days || [],
        consultationFee: d.consultation_fee || "",
        consultationFeeEn: (d as any).consultation_fee_en || (d as any).consultationFeeEn || "",
        showPrice: (d as any).show_price !== false,
        specializations: d.specializations || [],
        specializationsEn: (d as any).specializations_en || (d as any).specializationsEn || [],
        achievements: d.achievements || [],
        achievementsEn: (d as any).achievements_en || (d as any).achievementsEn || [],
        reviews: d.reviews || [],
      }));
      setDoctors(mapped);
    } catch (err) {
      setDoctorsError(err instanceof Error ? err.message : "فشل في تحميل الأطباء");
    } finally {
      setDoctorsLoading(false);
    }
  }, []);

  const reloadServices = useCallback(async () => {
    setServicesLoading(true);
    setServicesError(null);
    try {
      const data = await getServices();
      const colorMap: Record<string, string> = {
        teal: "from-brand-forest-900/90",
        amber: "from-amber-950/90",
        violet: "from-violet-950/90",
        cyan: "from-cyan-950/90",
        rose: "from-rose-950/90",
        green: "from-emerald-950/90",
        orange: "from-orange-950/90",
      };
      const mapped = data.map(s => {
        const accent = s.accent_color || "teal";
        return {
          id: s.id,
          service_id: s.service_id,
          name: s.name,
          nameEn: (s as any).name_en || (s as any).nameEn || "",
          name_en: (s as any).name_en || (s as any).nameEn || "",
          tagline: s.tagline || "",
          taglineEn: (s as any).tagline_en || (s as any).taglineEn || "",
          tagline_en: (s as any).tagline_en || (s as any).taglineEn || "",
          description: s.description || "",
          descriptionEn: (s as any).description_en || (s as any).descriptionEn || "",
          description_en: (s as any).description_en || (s as any).descriptionEn || "",
          longDescription: s.long_description || "",
          longDescriptionEn: (s as any).long_description_en || (s as any).longDescriptionEn || "",
          longDescription_en: (s as any).long_description_en || (s as any).longDescriptionEn || "",
          icon: s.icon || "",
          accentColor: accent,
          image: s.image || "",
          heroImage: s.hero_image || "",
          category: s.category || "",
          categoryEn: (s as any).category_en || (s as any).categoryEn || "",
          category_en: (s as any).category_en || (s as any).categoryEn || "",
          stats: s.stats || [],
          procedures: s.procedures || [],
          prices: s.prices || [],
          doctors: s.doctors || [],
          faqs: s.faqs || [],
          relatedServices: s.related_services || [],
          color: colorMap[accent] || "from-brand-forest-900/90",
          price: s.prices?.[0]?.price?.toString() || "",
          duration: s.prices?.[0]?.duration || "",
          status: (s as any).status || "active",
          showPrice: (s as any).show_price !== false,
        };
      });
      setServices(mapped);
    } catch (err) {
      setServicesError(err instanceof Error ? err.message : "فشل في تحميل الخدمات");
    } finally {
      setServicesLoading(false);
    }
  }, []);

  const reloadPackages = useCallback(async () => {
    setPackagesLoading(true);
    setPackagesError(null);
    try {
      const data = await getPackages();
      const mapped = data.map(p => {
        const incEn = (p as any).includes_en || [];
        const rawIncludes = (p.includes || []).map((inc: any, idx: number) => ({
          label: inc.label || "",
          labelEn: inc.label_en || inc.labelEn || incEn[idx]?.label || "",
          icon: inc.icon || "ri-check-line",
        }));
        const rawFaqs = (p.faqs || []).map((f: any) => ({
          q: f.q || f.question || "",
          qEn: f.q_en || f.question_en || f.qEn || "",
          q_en: f.q_en || f.question_en || f.qEn || "",
          a: f.a || f.answer || "",
          aEn: f.a_en || f.answer_en || f.aEn || "",
          a_en: f.a_en || f.answer_en || f.aEn || "",
        }));

        return {
          id: p.id,
          package_id: p.package_id,
          name: p.name,
          nameEn: (p as any).name_en || (p as any).nameEn || "",
          name_en: (p as any).name_en || (p as any).nameEn || "",
          category: p.category as PackageCategory,
          categoryEn: (p as any).category_en || (p as any).categoryEn || "",
          category_en: (p as any).category_en || (p as any).categoryEn || "",
          price: p.price,
          originalPrice: p.original_price,
          showPrice: (p as any).show_price !== false,
          badge: p.badge || "",
          badgeEn: (p as any).badge_en || (p as any).badgeEn || "",
          badge_en: (p as any).badge_en || (p as any).badgeEn || "",
          icon: p.icon || "",
          features: p.features || [],
          featuresEn: (p as any).features_en || (p as any).featuresEn || [],
          features_en: (p as any).features_en || (p as any).featuresEn || [],
          accentColor: p.accent_color || "teal",
          description: p.description || "",
          descriptionEn: (p as any).description_en || (p as any).descriptionEn || "",
          description_en: (p as any).description_en || (p as any).descriptionEn || "",
          duration: p.duration || "",
          durationEn: (p as any).duration_en || (p as any).durationEn || "",
          duration_en: (p as any).duration_en || (p as any).durationEn || "",
          targetAudience: p.target_audience || "",
          targetAudienceEn: (p as any).target_audience_en || (p as any).targetAudienceEn || "",
          target_audience_en: (p as any).target_audience_en || (p as any).targetAudienceEn || "",
          preparation: p.preparation || [],
          preparationEn: (p as any).preparation_en || (p as any).preparationEn || [],
          preparation_en: (p as any).preparation_en || (p as any).preparationEn || [],
          includes: rawIncludes,
          includesEn: incEn,
          faqs: rawFaqs,
          status: p.is_active === false || (p as any).status === "inactive" ? "inactive" : ((p as any).status || "active"),
          is_active: p.is_active !== false && (p as any).status !== "inactive",
          image: (p as any).image || "",
        };
      });
      setPackages(mapped);
    } catch (err) {
      setPackagesError(err instanceof Error ? err.message : "فشل في تحميل الباقات");
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  const reloadBlog = useCallback(async () => {
    setBlogLoading(true);
    setBlogError(null);
    try {
      const [cats, backendPosts] = await Promise.all([
        getCategories(),
        getPosts("all")
      ]);
      setBlogCategories(cats);
      const adapted = backendPosts.map(p => adaptPost(p, cats));
      setAllPosts(adapted);
    } catch (err) {
      setBlogError(err instanceof Error ? err.message : "فشل في تحميل المقالات");
    } finally {
      setBlogLoading(false);
    }
  }, []);

  const reloadHomeContent = useCallback(async () => {
    setHomeContentLoading(true);
    try {
      const data = await getHomeContent();
      if (data && data.hero && data.whyUs) {
        setHomeContent(data);
        writeLS("cms_home_data", data);
        if (data.testimonials) {
          const rawItems = Array.isArray(data.testimonials.items) 
            ? data.testimonials.items 
            : (Array.isArray(data.testimonials) ? data.testimonials : []);
          
          const mapped = rawItems.map((t: any, idx: number) => ({
            id: t.id || `t-${idx}`,
            name: t.name || "",
            nameEn: t.name_en || t.nameEn || "",
            specialty: t.specialty || t.role || "",
            specialtyEn: t.specialty_en || t.specialtyEn || "",
            text: t.text || "",
            textEn: t.text_en || t.textEn || "",
            rating: t.rating || 5,
            image: t.image || t.avatar || "",
            date: t.date || "",
            service: t.service || "",
            serviceEn: t.service_en || t.serviceEn || "",
            verified: t.verified ?? true,
            published: t.published ?? true,
            featured: t.featured ?? true,
          }));
          setTestimonials(mapped);

          const cfg = {
            sectionBadge: data.testimonials.badge || (data.testimonials.config as any)?.sectionBadge || "تجارب مرضانا",
            sectionBadgeEn: data.testimonials.badge_en || (data.testimonials.config as any)?.sectionBadgeEn || (data.testimonials.config as any)?.sectionBadge_en || "Patient Stories",
            sectionTitle: data.testimonials.heading || (data.testimonials.config as any)?.sectionTitle || "ماذا يقول من اختاروا الفهم الحقيقي؟",
            sectionTitleEn: data.testimonials.heading_en || (data.testimonials.config as any)?.sectionTitleEn || (data.testimonials.config as any)?.sectionTitle_en || "What Do Those Who Chose True Understanding Say?",
            sectionSubtitle: data.testimonials.description || (data.testimonials.config as any)?.sectionSubtitle || "مرضى لم تمنحهم الزيارات السريعة صورة واضحة — حتى جاؤوا إلى ذا مديكال أفينيو",
            sectionSubtitleEn: data.testimonials.description_en || (data.testimonials.config as any)?.sectionSubtitleEn || (data.testimonials.config as any)?.sectionSubtitle_en || "Patients who did not get a clear picture from rushed visits — until they came to The Medical Avenue",
            displayStyle: data.testimonials.config?.displayStyle || "slider",
            showRating: data.testimonials.config?.showRating ?? true,
            showImage: data.testimonials.config?.showImage ?? true,
          };
          setTestimonialsConfig(cfg);
        }
      }
    } catch (err) {
      console.error("Failed to load CMS home content from API", err);
    } finally {
      setHomeContentLoading(false);
    }
  }, []);

  const reloadFAQs = useCallback(async () => {
    setFaqsLoading(true);
    try {
      const [fetchedFaqs, fetchedCategories] = await Promise.all([
        getFaqs(),
        getFaqCategories()
      ]);
      setFaqs(fetchedFaqs);
      setFaqCategories(fetchedCategories);
    } catch (err) {
      console.error(err);
    } finally {
      setFaqsLoading(false);
    }
  }, []);

  const reloadAbout = useCallback(async () => {
    setAboutLoading(true);
    try {
      const data = await getAboutCMS();
      const hero = data.hero || {};
      setAboutHero({
        ...defaultAboutHero,
        ...hero,
        badgeEn: hero.badgeEn || hero.badge_en,
        titleEn: hero.titleEn || hero.title_en,
        subtitleEn: hero.subtitleEn || hero.subtitle_en,
        descriptionEn: hero.descriptionEn || hero.description_en,
        typewriterWordsEn: hero.typewriterWordsEn || hero.typewriterWords_en,
        ctaPrimaryEn: hero.ctaPrimaryEn || hero.ctaPrimary_en,
        ctaSecondaryEn: hero.ctaSecondaryEn || hero.ctaSecondary_en,
      });
      const story = data.story || {};
      setAboutStory({
        ...defaultAboutStory,
        ...story,
        badgeEn: story.badgeEn || story.badge_en,
        titleEn: story.titleEn || story.title_en,
        paragraphsEn: story.paragraphsEn || story.paragraphs_en,
        tagsEn: story.tagsEn || story.tags_en,
      });
      setAboutMission((data.mission || []).map((m: any) => ({
        ...m,
        titleEn: m.titleEn || m.title_en,
        bodyEn: m.bodyEn || m.body_en,
      })));
      setAboutValues((data.values || []).map((v: any) => ({
        ...v,
        titleEn: v.titleEn || v.title_en,
        descriptionEn: v.descriptionEn || v.description_en,
      })));
      setAboutTimeline((data.timeline || []).map((t: any) => ({
        ...t,
        titleEn: t.titleEn || t.title_en,
        descEn: t.descEn || t.desc_en,
      })));
      setAboutTeam((data.team || []).map((tm: any) => ({
        ...tm,
        nameEn: tm.nameEn || tm.name_en,
        roleEn: tm.roleEn || tm.role_en,
        experienceEn: tm.experienceEn || tm.experience_en,
      })));
      setAboutAwards((data.awards || []).map((a: any) => ({
        ...a,
        titleEn: a.titleEn || a.title_en,
        bodyEn: a.bodyEn || a.body_en,
      })));
    } catch (err) {
      console.error("Failed to fetch About CMS", err);
    } finally {
      setAboutLoading(false);
    }
  }, []);

  const reloadContact = useCallback(async () => {
    setContactLoading(true);
    try {
      const data = await getContactCMS();
      const hero = data.hero || {};
      setContactHero({
        ...defaultContactHero,
        ...hero,
        badgeEn: hero.badgeEn || hero.badge_en,
        titleEn: hero.titleEn || hero.title_en,
        descriptionEn: hero.descriptionEn || hero.description_en,
        typewriterWordsEn: hero.typewriterWordsEn || hero.typewriterWords_en,
      });
      setContactMethods((data.methods || []).map((m: any) => ({
        ...m,
        titleEn: m.titleEn || m.title_en,
        subEn: m.subEn || m.sub_en,
        valueEn: m.valueEn || m.value_en,
      })));
      const formCfg = data.form_config || {};
      setContactFormConfig({
        ...defaultContactForm,
        ...formCfg,
        titleEn: formCfg.titleEn || formCfg.title_en,
        subtitleEn: formCfg.subtitleEn || formCfg.subtitle_en,
        successTitleEn: formCfg.successTitleEn || formCfg.successTitle_en,
        successMessageEn: formCfg.successMessageEn || formCfg.successMessage_en,
        subjectsEn: formCfg.subjectsEn || formCfg.subjects_en,
        fields: {
          ...defaultContactForm.fields,
          ...(formCfg.fields || {})
        }
      });
      const mapCfg = data.map_config || {};
      setContactMapConfig({
        ...defaultContactMap,
        ...mapCfg,
        titleEn: mapCfg.titleEn || mapCfg.title_en,
        mapsLinkTextEn: mapCfg.mapsLinkTextEn || mapCfg.mapsLinkText_en,
      });
      const ctaCfg = data.cta_banner || {};
      setContactCtaBanner({
        ...defaultContactCta,
        ...ctaCfg,
        titleEn: ctaCfg.titleEn || ctaCfg.title_en,
        descriptionEn: ctaCfg.descriptionEn || ctaCfg.description_en,
        buttonTextEn: ctaCfg.buttonTextEn || ctaCfg.buttonText_en,
      });
      const faqCfg = data.faq_teaser || {};
      setContactFaqTeaser({
        ...defaultContactFaq,
        ...faqCfg,
        titleEn: faqCfg.titleEn || faqCfg.title_en,
        descriptionEn: faqCfg.descriptionEn || faqCfg.description_en,
        buttonTextEn: faqCfg.buttonTextEn || faqCfg.buttonText_en,
      });
    } catch (err) {
      console.error("Failed to fetch Contact CMS", err);
    } finally {
      setContactLoading(false);
    }
  }, []);

  const reloadSearch = useCallback(async () => {
    setSearchLoading(true);
    try {
      const data = await getSearchCMS();
      setSearchHero(data.hero || defaultSearchHero);
      setSearchPopular(data.popular_searches || []);
      setSearchQuickLinks(data.quick_links || []);
      setSearchCTA(data.cta || defaultSearchCTA);
      setSearchResultsConfig(data.results_config || defaultSearchResultsConfig);
    } catch (err) {
      console.error("Failed to fetch Search CMS", err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const reloadOffers = useCallback(async () => {
    setOffersLoading(true);
    try {
      const data = await getOffersPageConfig();
      setOffersHero(data.hero || defaultOffersHero);
      setOffersRedeem(data.how_to_redeem || []);
      setOffersNotify(data.notify || defaultOffersNotify);
    } catch (err) {
      console.error("Failed to fetch Offers CMS", err);
    } finally {
      setOffersLoading(false);
    }
  }, []);

  const reloadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const data = await getSettings();
      const parseSections = (val: any) => {
        if (Array.isArray(val)) return val;
        if (typeof val === "string") {
          try {
            const parsed = JSON.parse(val);
            if (Array.isArray(parsed)) return parsed;
          } catch {}
        }
        return [];
      };

      setClinicInfo({
        name: data.clinic_name || "",
        nameEn: data.clinic_name_en || "",
        logo: data.appearance?.logo || "",
        email: data.email || "",
        phone: data.phone || "",
        whatsapp: data.whatsapp || "",
        address: data.address || "",
        addressEn: data.address_en || "",
        terms: data.terms || "",
        termsEn: data.terms_en || "",
        privacy: data.privacy || "",
        privacyEn: data.privacy_en || "",
        termsSections: parseSections(data.terms_sections),
        privacySections: parseSections(data.privacy_sections),
        mapEmbedUrl: data.appearance?.mapEmbedUrl || "",
        branches: data.appearance?.branches || [
          { id: "b-1", name: "الفرع الرئيسي - العليا", nameEn: "Main Branch - Olaya", address: "شارع الملك فهد، حي العليا، الرياض", addressEn: "King Fahd Road, Al Olaya, Riyadh", phone: "011-4567890", isMain: true },
          { id: "b-2", name: "فرع النخيل", nameEn: "Al Nakheel Branch", address: "طريق الملك عبدالله، حي النخيل، الرياض", addressEn: "King Abdullah Road, Al Nakheel, Riyadh", phone: "011-4567891", isMain: false },
        ],
        directionsNote: data.appearance?.directionsNote || "نقع في شارع الملك فهد، بجانب برج المملكة. يمكن الوصول إلينا عبر مترو الرياض محطة العليا. يتوفر موقف سيارات مجاني للمرضى.",
        directionsNoteEn: data.appearance?.directionsNoteEn || "Located on King Fahd Road next to Kingdom Tower. Free valet parking available.",
      });
      setClinicHours(Array.isArray(data.working_hours) ? data.working_hours : defaultWorkingHours);
      setClinicSocial(data.appearance?.social || defaultSocialMedia);
      setClinicEmergency({
        phone: data.phone || "",
        text: data.whatsapp || "",
      });
      setClinicSeo({ ...defaultSeoSettings, ...(data.seo || {}) });
      setClinicPixels({ ...defaultPixelSettings, ...(data.pixels || {}) });
      setClinicSecurity({ ...defaultSecuritySettings, ...(data.security || {}) });
    } catch (err) {
      console.error("Failed to fetch Settings CMS", err);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadServices();
    reloadPackages();
    reloadDoctors();
    reloadBlog();
    reloadFAQs();
    reloadAbout();
    reloadContact();
    reloadSearch();
    reloadOffers();
    reloadSettings();
    reloadHomeContent();
  }, [reloadServices, reloadPackages, reloadDoctors, reloadBlog, reloadFAQs, reloadAbout, reloadContact, reloadSearch, reloadOffers, reloadSettings, reloadHomeContent]);

  // Listen to custom updates
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const key = detail?.key;
      if (!key) return;

      if (key === "doctors") reloadDoctors();
      if (key === "services") reloadServices();
      if (key === "packages") reloadPackages();
      if (key === "blog") reloadBlog();
      if (key === "faqs") reloadFAQs();
      if (key === "cms_home_data" || key === "home") reloadHomeContent();
      
      // We can map keys to reloads or just reload everything
      if (key.startsWith("about_")) reloadAbout();
      if (key.startsWith("contact_")) reloadContact();
      if (key.startsWith("search_")) reloadSearch();
      if (key.startsWith("offers_")) reloadOffers();
      if (key.startsWith("cms_settings_")) reloadSettings();
    };
    window.addEventListener("cms-update", handler);
    return () => window.removeEventListener("cms-update", handler);
  }, [reloadDoctors, reloadServices, reloadPackages, reloadBlog, reloadHomeContent, reloadFAQs, reloadAbout, reloadContact, reloadSearch, reloadOffers, reloadSettings]);


  // --- Savers ---
  const saveFAQs = async (newFaqs: FAQItem[], newCats: FAQCategory[]) => {
    await massSaveFaqs(newFaqs, faqs, newCats, faqCategories);
    await reloadFAQs();
    window.dispatchEvent(new CustomEvent("cms-update", { detail: { key: "faqs" } }));
  };
  const resetFAQs = () => {};

  const saveAbout = async (data: any) => {
    const token = localStorage.getItem("access_token") || "";
    await updateAboutCMS(data, token);
    await reloadAbout();
    window.dispatchEvent(new CustomEvent("cms-update", { detail: { key: "about_hero" } }));
  };

  const saveContact = async (data: any) => {
    const token = localStorage.getItem("access_token") || "";
    await updateContactCMS({
      hero: data.hero,
      methods: data.methods,
      form_config: data.form,
      map_config: data.map,
      cta_banner: data.cta,
      faq_teaser: data.faq
    }, token);
    await reloadContact();
    window.dispatchEvent(new CustomEvent("cms-update", { detail: { key: "contact_hero" } }));
  };

  const saveSearch = async (data: any) => {
    const token = localStorage.getItem("access_token") || "";
    await updateSearchCMS({
      hero: data.hero,
      popular_searches: data.popular,
      quick_links: data.quickLinks,
      cta: data.cta,
      results_config: data.resultsConfig
    }, token);
    await reloadSearch();
    window.dispatchEvent(new CustomEvent("cms-update", { detail: { key: "search_hero" } }));
  };

  const saveOffers = async (data: any) => {
    const token = localStorage.getItem("access_token") || "";
    await updateOffersPageConfig({
      hero: data.hero,
      how_to_redeem: data.redeem,
      notify: data.notify
    }, token);
    await reloadOffers();
    window.dispatchEvent(new CustomEvent("cms-update", { detail: { key: "offers_hero" } }));
  };

  const saveSettings = async (data: any) => {
    const token = localStorage.getItem("access_token") || "";
    await updateSettings({
      clinic_name: data.info.name,
      clinic_name_en: data.info.nameEn,
      phone: data.info.phone,
      email: data.info.email,
      whatsapp: data.emergency.text,
      address: data.info.address,
      address_en: data.info.addressEn,
      terms: data.info.terms,
      terms_en: data.info.termsEn,
      privacy: data.info.privacy,
      privacy_en: data.info.privacyEn,
      terms_sections: data.info.termsSections,
      privacy_sections: data.info.privacySections,
      working_hours: data.hours,
      maintenance_mode: false,
      seo: data.seo,
      pixels: data.pixels,
      security: data.security,
      appearance: {
        logo: data.info.logo,
        mapEmbedUrl: data.info.mapEmbedUrl,
        social: data.social,
        branches: data.info.branches,
        directionsNote: data.info.directionsNote,
        directionsNoteEn: data.info.directionsNoteEn,
      }
    }, token);
    await reloadSettings();
    window.dispatchEvent(new CustomEvent("cms-update", { detail: { key: "cms_settings_info" } }));
  };

  const saveTestimonials = async (data: any, config: any) => {
    const updatedHomeContent = {
      ...homeContentState,
      testimonials: {
        ...homeContentState.testimonials,
        items: data,
        config: config
      }
    };
    const updated = await updateHomeContent(updatedHomeContent);
    setHomeContent(updated);
    setTestimonials(data);
    setTestimonialsConfig(config);
    window.dispatchEvent(new CustomEvent("cms-update", { detail: { key: "cms_home_data" } }));
  };
  const resetTestimonials = () => {};

  const saveHomeContent = async (data: CMSContent) => {
    const updated = await updateHomeContent(data);
    setHomeContent(updated);
    window.dispatchEvent(new CustomEvent("cms-update", { detail: { key: "cms_home_data" } }));
  };
  const resetHomeContent = () => {};

  const posts = allPosts.filter(p => p.status === "published");

  return (
    <DataContext.Provider
      value={{
        doctors, doctorsLoading, doctorsError, reloadDoctors,
        services, servicesLoading, servicesError, reloadServices,
        packages, packagesLoading, packagesError, reloadPackages,
        posts, allPosts, blogCategories, blogLoading, blogError, reloadBlog,
        faqs, faqCategories, faqsLoading, faqsError, reloadFAQs, saveFAQs, resetFAQs,
        
        aboutHero: aboutHeroState, aboutStory: aboutStoryState, aboutMission: aboutMissionState, aboutValues: aboutValuesState, aboutTimeline: aboutTimelineState, aboutTeam: aboutTeamState, aboutAwards: aboutAwardsState,
        aboutLoading, reloadAbout, saveAbout,

        contactHero: contactHeroState, contactMethods: contactMethodsState, contactFormConfig: contactFormConfigState, contactMapConfig: contactMapConfigState, contactWorkingHours: contactWorkingHoursState, contactSocialLinks: contactSocialLinksState, contactCtaBanner: contactCtaBannerState, contactFaqTeaser: contactFaqTeaserState,
        contactLoading, reloadContact, saveContact,

        offersHero: offersHeroState, offersSeasonal: offersSeasonalState, offersFlash: offersFlashState, offersRedeem: offersRedeemState, offersNotify: offersNotifyState,
        offersLoading, reloadOffers, saveOffers,

        clinicInfo: clinicInfoState, clinicHours: clinicHoursState, clinicSocial: clinicSocialState, clinicEmergency: clinicEmergencyState, clinicSeo: clinicSeoState, clinicPixels: clinicPixelsState, clinicSecurity: clinicSecurityState,
        settingsLoading, reloadSettings, saveSettings,

        testimonials: testimonialsState, testimonialsConfig: testimonialsConfigState, testimonialsStats: initialTestimonialsStats, saveTestimonials, resetTestimonials,

        searchHero: searchHeroState, searchPopular: searchPopularState, searchQuickLinks: searchQuickLinksState, searchCTA: searchCTAState, searchResultsConfig: searchResultsConfigState,
        searchLoading, reloadSearch, saveSearch,

        // Homepage CMS
        homeContent: homeContentState, homeContentLoading, reloadHomeContent, saveHomeContent, resetHomeContent,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useDataContext must be used within a DataProvider");
  }
  return context;
};
